import { Component, computed, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { AccountingPeriodSummary } from '../../../../../../../api';
import { AccountingPeriodFormModel } from './models/accounting-period-form-model';
import { AccountingPeriodQueryService } from './services/accounting-period-query.service';
import { AccountingPeriodCommandService } from './services/accounting-period-command.service';
import { ElciaDatePicker } from '../../../../../../components/date-picker/date-picker';
import { CheckBox } from '../../../../../../components/check-box/check-box';
import { ElciaButton } from '../../../../../../components/buttons/button/button';

@Component({
  selector: 'elcia-accounting-settings-periods',
  imports: [ElciaDatePicker, CheckBox, ElciaButton, TranslatePipe],
  providers: [AccountingPeriodQueryService, AccountingPeriodCommandService],
  templateUrl: './accounting-settings-periods.html',
  styleUrl: './accounting-settings-periods.scss',
})
export class AccountingSettingsPeriods {

  private readonly queryService = inject(AccountingPeriodQueryService);
  private readonly commandService = inject(AccountingPeriodCommandService);

  isLoading = computed(() => this.queryService.isLoading());

  periodForms = computed(() => {
    const formArray = new FormArray<FormGroup<AccountingPeriodFormModel>>([]);
    for (const period of this.queryService.periods()) {
      const fg = this.createFormGroup(period);
      fg.disable();
      formArray.push(fg);
    }
    return formArray;
  });

  createForm = new FormGroup<Omit<AccountingPeriodFormModel, 'id'>>({
    startDate: new FormControl<Date | null>(null, Validators.required),
    endDate: new FormControl<Date | null>(null, Validators.required),
    isClosed: new FormControl<boolean | null>(false),
  });

  private createFormGroup(period: AccountingPeriodSummary): FormGroup<AccountingPeriodFormModel> {
    return new FormGroup<AccountingPeriodFormModel>({
      id: new FormControl(period.id),
      startDate: new FormControl<Date | null>(period.startDate, Validators.required),
      endDate: new FormControl<Date | null>(period.endDate, Validators.required),
      isClosed: new FormControl<boolean | null>(period.isClosed),
    });
  }

  private getFormGroupById(id: number | null | undefined) {
    return this.periodForms().controls.find(c => c.value.id === id);
  }

  toggleFormGroup(id: number | null | undefined) {
    const fg = this.getFormGroupById(id);
    if (!fg) return;
    if (fg.enabled) {
      if (fg.invalid) return;
      this.commandService.updateMutation.mutate({
        id: fg.value.id!,
        body: {
          startDate: fg.value.startDate!,
          endDate: fg.value.endDate!,
          isClosed: fg.value.isClosed ?? false,
        },
      });
      fg.disable();
    } else {
      fg.enable();
    }
  }

  submitCreate() {
    if (this.createForm.invalid) return;
    this.commandService.createMutation.mutate(
      {
        startDate: this.createForm.value.startDate!,
        endDate: this.createForm.value.endDate!,
        isClosed: this.createForm.value.isClosed ?? false,
      },
      { onSuccess: () => this.createForm.reset() }
    );
  }
}
